/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * Entity-Aware Profile Settings
 * Shows different fields based on account type (individual, organization, university, institute)
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Save, User, Briefcase, Mail, Calendar, Shield, Smartphone, Key,
  CheckCircle, XCircle, Copy, Eye, EyeOff, AlertTriangle, Building,
  MapPin, FileText, Plus, X, GraduationCap, BookOpen
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

// Entity-specific field configurations
const ENTITY_FIELDS = {
  individual: [
    { field: 'current_role', label: 'Current Role', type: 'text', placeholder: 'e.g., Senior WMS Consultant' },
    { field: 'organisation', label: 'Company / Organisation', type: 'text', placeholder: 'Where you work (optional)' },
    { field: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com', pattern: '^https://.*' },
    { field: 'years_experience', label: 'Years of Experience', type: 'number', min: 0, max: 50 },
    { field: 'skills', label: 'Skills', type: 'tags', placeholder: 'Add a skill...' },
    { field: 'certifications', label: 'Certifications', type: 'tags', placeholder: 'Add a certification...' }
  ],
  organization: [
    { field: 'company_name', label: 'Company Name', type: 'text', placeholder: 'Your company name' },
    { field: 'industry', label: 'Industry', type: 'text', placeholder: 'e.g., Logistics, Manufacturing' },
    { field: 'company_size', label: 'Company Size', type: 'select', options: ['1-10', '11-50', '51-200', '201-500', '500+'] },
    { field: 'website', label: 'Website', type: 'url', placeholder: 'https://example.com' },
    { field: 'departments', label: 'Departments', type: 'tags', placeholder: 'Add a department...' },
    { field: 'technologies', label: 'Technologies Used', type: 'tags', placeholder: 'Add a technology...' }
  ],
  university: [
    { field: 'institution_name', label: 'Institution Name', type: 'text', placeholder: 'University name' },
    { field: 'institution_type', label: 'Type', type: 'select', options: ['Public', 'Private', 'Deemed', 'Autonomous'] },
    { field: 'programs', label: 'Programs Offered', type: 'tags', placeholder: 'Add a program...' },
    { field: 'faculty_count', label: 'Faculty Count', type: 'number', min: 0 },
    { field: 'research_areas', label: 'Research Areas', type: 'tags', placeholder: 'Add a research area...' },
    { field: 'placement_rate', label: 'Placement Rate %', type: 'number', min: 0, max: 100 }
  ],
  institute: [
    { field: 'institute_name', label: 'Institute Name', type: 'text', placeholder: 'Institute name' },
    { field: 'certifications_offered', label: 'Certifications Offered', type: 'tags', placeholder: 'Add a certification...' },
    { field: 'courses', label: 'Courses', type: 'tags', placeholder: 'Add a course...' },
    { field: 'trainers_count', label: 'Number of Trainers', type: 'number', min: 0 },
    { field: 'accreditations', label: 'Accreditations', type: 'tags', placeholder: 'Add an accreditation...' }
  ]
};

const ENTITY_LABELS = {
  individual: { title: 'Personal Profile', icon: User, color: 'blue' },
  organization: { title: 'Organization Profile', icon: Building, color: 'purple' },
  university: { title: 'University Profile', icon: GraduationCap, color: 'green' },
  institute: { title: 'Institute Profile', icon: BookOpen, color: 'orange' }
};

// Tags input component
const TagsInput = ({ value = [], onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!value.includes(inputValue.trim())) {
        onChange([...value, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  return (
    <div className="border border-gray-300 rounded-md p-2 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map((tag, idx) => (
          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
            {tag}
            <button type="button" onClick={() => removeTag(tag)} className="hover:text-blue-600">
              <X size={14} />
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : 'Add more...'}
        className="w-full outline-none text-sm"
      />
    </div>
  );
};

const ProfileSettings = () => {
  const { user: authUser } = useAuth();
  const user = authUser || JSON.parse(localStorage.getItem('user') || '{}');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get entity type (account_type, entityType, or default to individual)
  const entityType = user.account_type || user.entityType || 'individual';

  // Form state - common fields
  const [formData, setFormData] = useState({
    name: user.name || user.fullName || '',
    email: user.email || '',
    vertical: user.vertical || 'WMS',
    phone: user.phone || '',
    bio: user.bio || '',
    location: user.location || ''
  });

  // Entity-specific data
  const [entityData, setEntityData] = useState(user.entity_data || {});

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Check if 2FA is mandatory for this user (non-individual accounts)
  const isNonIndividual = entityType && entityType !== 'individual';
  const twoFactorMandatory = user.twoFactorMandatory || isNonIndividual;
  const requires2FASetup = searchParams.get('setup2fa') === 'required';

  // 2FA State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(true);
  const [setupStep, setSetupStep] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [manualKey, setManualKey] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [twoFactorError, setTwoFactorError] = useState(null);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ── Delete Account State ──────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteTwoFactorCode, setDeleteTwoFactorCode] = useState('');


  // ShubbhYatra: default tracks match today's hardcoded list. 'Travel'
  // is appended only if GET /api/public/verticals?email=<self> says yes
  // (because the authed user owns the email or has a pending invite).
  // One-shot fetch — email is immutable on the profile edit screen.
  const DEFAULT_TRACKS = [
    { value: 'WMS', label: 'Warehouse Management Systems', icon: '📦' },
    { value: 'iSeries', label: 'IBM iSeries/AS400', icon: '💻' },
    { value: 'Civil', label: 'Civil Engineering & Construction', icon: '🏗️' },
    { value: 'Stitching', label: 'Apparel & Stitching', icon: '🧵' },
    { value: 'ERPNext', label: 'ERPNext (ERP System)', icon: '🏭' },
    { value: 'SkillCode', label: 'AI Software Engineering', icon: '🖥️' }
  ];
  const [tracks, setTracks] = useState(DEFAULT_TRACKS);
  useEffect(() => {
    const email = (formData.email || '').trim().toLowerCase();
    if (!email) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/public/verticals?email=${encodeURIComponent(email)}`);
        const list = res?.data?.data?.verticals;
        if (cancelled || !Array.isArray(list) || !list.length) return;
        // Preserve the richer labels/icons from DEFAULT_TRACKS; only append
        // Travel when the server says so.
        const next = DEFAULT_TRACKS.slice();
        const hasTravel = list.some((v) => v.value === 'Travel');
        if (hasTravel && !next.some((v) => v.value === 'Travel')) {
          // Icon is a plane glyph via Unicode escape — avoids any emoji
          // variation-selector ambiguity when the file is round-tripped
          // through tooling.
          next.push({ value: 'Travel', label: 'यात्रा / Travel', icon: '✈' });
        }
        setTracks(next);
      } catch (err) {
        // Leave default list in place.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  // ── Self Deletion Handler ─────────────────────────────────────
const handleSelfDelete = async () => {
  setDeleteError('');

  if (deleteConfirm !== 'DELETE MY ACCOUNT') {
    setDeleteError('Please type exactly: DELETE MY ACCOUNT');
    return;
  }
  if (!deletePassword) {
    setDeleteError('Password is required');
    return;
  }
  // If 2FA enabled — require code
  if (twoFactorEnabled && !deleteTwoFactorCode) {
    setDeleteError('2FA code is required');
    return;
  }
  if (twoFactorEnabled && deleteTwoFactorCode.length !== 6) {
    setDeleteError('Enter your 6-digit 2FA code');
    return;
  }

  try {
    setDeleteLoading(true);
    const data = await api.post('/users/me/delete', {
      password: deletePassword,
      confirmation: deleteConfirm,
      twoFactorCode: twoFactorEnabled ? deleteTwoFactorCode : undefined
    });

    if (data.success) {
      setDeleteStep(3);
      setTimeout(() => {
        localStorage.clear();
        navigate('/login', {
          state: {
            message: 'Your account has been permanently deleted. Thank you for using SkillDelivery.'
          }
        });
      }, 3000);
    } else {
      setDeleteError(data.error || 'Deletion failed — please try again');
      setDeleteLoading(false);
    }
  } catch (err) {
    setDeleteError('Network error — please try again');
    setDeleteLoading(false);
  }
};

  // Fetch 2FA status on mount
  useEffect(() => {
    const fetch2FAStatus = async () => {
      try {
        const response = await api.get('/two-factor/status');
        if (response.success) {
          const enabled = response.data.enabled && response.data.verified;
          setTwoFactorEnabled(enabled);
          if (requires2FASetup && !enabled) {
            setTimeout(() => start2FASetup(), 500);
          }
        }
      } catch (error) {
        console.error('Error fetching 2FA status:', error);
      } finally {
        setTwoFactorLoading(false);
      }
    };
    fetch2FAStatus();
  }, [requires2FASetup]);

  // 2FA functions
  const start2FASetup = async () => {
    setTwoFactorError(null);
    setTwoFactorLoading(true);
    try {
      const response = await api.post('/two-factor/setup');
      if (response.success) {
        setQrCode(response.data.qrCode);
        setManualKey(response.data.manualEntryKey);
        setSetupStep('qr');
      } else {
        setTwoFactorError(response.error || 'Failed to start 2FA setup');
      }
    } catch (error) {
      setTwoFactorError(error.message || 'Failed to start 2FA setup');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const verify2FASetup = async (e) => {
    e.preventDefault();
    setTwoFactorError(null);
    setTwoFactorLoading(true);
    try {
      const response = await api.post('/two-factor/verify-setup', { token: verificationCode });
      if (response.success) {
        setBackupCodes(response.data.backupCodes);
        setSetupStep('backup');
        setTwoFactorEnabled(true);
        const updatedUser = { ...user, twoFactorEnabled: true };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        setTwoFactorError(response.error || 'Invalid verification code');
      }
    } catch (error) {
      setTwoFactorError(error.message || 'Failed to verify code');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const disable2FA = async (e) => {
    e.preventDefault();
    setTwoFactorError(null);
    setTwoFactorLoading(true);
    try {
      const response = await api.post('/two-factor/disable', { password: disablePassword, token: disableCode });
      if (response.success) {
        setTwoFactorEnabled(false);
        setShowDisableModal(false);
        setDisablePassword('');
        setDisableCode('');
        setTwoFactorSuccess('Two-factor authentication has been disabled');
        const updatedUser = { ...user, twoFactorEnabled: false };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setTimeout(() => setTwoFactorSuccess(null), 3000);
      } else {
        setTwoFactorError(response.error || 'Failed to disable 2FA');
      }
    } catch (error) {
      setTwoFactorError(error.message || 'Failed to disable 2FA');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setTwoFactorSuccess('Backup codes copied to clipboard');
    setTimeout(() => setTwoFactorSuccess(null), 2000);
  };

  const finish2FASetup = () => {
    setSetupStep(null);
    setQrCode(null);
    setManualKey(null);
    setVerificationCode('');
    setBackupCodes([]);
    setTwoFactorSuccess('Two-factor authentication is now enabled!');
    setTimeout(() => setTwoFactorSuccess(null), 3000);
  };

  const cancel2FASetup = () => {
    setSetupStep(null);
    setQrCode(null);
    setManualKey(null);
    setVerificationCode('');
    setTwoFactorError(null);
  };

  // Form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEntityDataChange = (field, value) => {
    setEntityData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');

    try {
      const response = await api.put(`/users/${user.id}`, {
        fullName: formData.name,
        phone: formData.phone,
        vertical: formData.vertical,
        bio: formData.bio,
        location: formData.location,
        entity_data: entityData
      });

      // api.put() returns JSON directly, not wrapped in { data: ... }
      // Backend returns { success: true, data: user }
      if (response.success) {
        const updatedUser = {
          ...user,
          fullName: formData.name,
          name: formData.name,
          phone: formData.phone,
          vertical: formData.vertical,
          bio: formData.bio,
          location: formData.location,
          entity_data: entityData
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setSaveMessage('Profile updated successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage(response.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveMessage(error.message || 'Error updating profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Render entity-specific field
  const renderEntityField = (fieldConfig) => {
    const { field, label, type, placeholder, options, min, max, pattern } = fieldConfig;
    const value = entityData[field] || (type === 'tags' ? [] : '');

    switch (type) {
      case 'text':
      case 'url':
        return (
          <input
            type={type}
            value={value}
            onChange={(e) => handleEntityDataChange(field, e.target.value)}
            placeholder={placeholder}
            pattern={pattern}
            title={type === 'url' && pattern ? 'Must start with https://' : undefined}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleEntityDataChange(field, parseInt(e.target.value) || 0)}
            min={min}
            max={max}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleEntityDataChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select...</option>
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'tags':
        return (
          <TagsInput
            value={value}
            onChange={(tags) => handleEntityDataChange(field, tags)}
            placeholder={placeholder}
          />
        );
      default:
        return null;
    }
  };

  const entityConfig = ENTITY_LABELS[entityType] || ENTITY_LABELS.individual;
  const entityFields = ENTITY_FIELDS[entityType] || ENTITY_FIELDS.individual;
  const EntityIcon = entityConfig.icon;

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Profile Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <User className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">Profile Settings</h2>
        </div>

        {/* Entity Type Badge */}
        <div className="mb-6 p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div className="flex items-center gap-2">
            <EntityIcon className="w-5 h-5 text-gray-600" />
            <span className="font-medium text-gray-800">{entityConfig.title}</span>
            {user.workspaceName && (
              <span className="ml-2 text-sm text-gray-600">• {user.workspaceName}</span>
            )}
          </div>
        </div>

        {saveMessage && (
          <div className={`mb-4 p-3 rounded-md ${
            saveMessage.includes('success')
              ? 'bg-green-50 text-green-600 border border-green-200'
              : 'bg-red-50 text-red-600 border border-red-200'
          }`}>
            {saveMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Common Fields */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">Basic Information</h3>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Full Name
                </div>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </div>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Phone
                </div>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+91 98765 43210"
              />
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Location
                </div>
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="City, Country"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  About / Bio
                </div>
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description about yourself or your organization..."
              />
            </div>

            {/* Primary Track */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Primary Track / Domain
                </div>
              </label>
              <select
                name="vertical"
                value={formData.vertical}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {tracks.map((track) => (
                  <option key={track.value} value={track.value}>
                    {track.icon} {track.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Entity-Specific Fields */}
          <div className="space-y-4 pt-4">
            <h3 className="text-lg font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
              <EntityIcon className="w-5 h-5" />
              {entityConfig.title} Details
            </h3>

            {entityFields.map((fieldConfig) => (
              <div key={fieldConfig.field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {fieldConfig.label}
                </label>
                {renderEntityField(fieldConfig)}
              </div>
            ))}
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSaving}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-6 h-6 text-green-600" />
          <h2 className="text-2xl font-bold text-gray-800">Security Settings</h2>
        </div>

        {twoFactorSuccess && (
          <div className="mb-4 p-3 rounded-md bg-green-50 text-green-600 border border-green-200 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {twoFactorSuccess}
          </div>
        )}
        {twoFactorError && (
          <div className="mb-4 p-3 rounded-md bg-red-50 text-red-600 border border-red-200 flex items-center gap-2">
            <XCircle className="w-4 h-4" />
            {twoFactorError}
          </div>
        )}

        {requires2FASetup && !twoFactorEnabled && (
          <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">2FA Setup Required</p>
                <p className="text-sm text-amber-700 mt-1">
                  Two-factor authentication is mandatory for {entityType} accounts.
                </p>
              </div>
            </div>
          </div>
        )}

        {isNonIndividual && (
          <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                <strong className="capitalize">{entityType}</strong> account
                {user.workspaceName && ` - ${user.workspaceName}`}
              </span>
              <span className="ml-auto text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                2FA Mandatory
              </span>
            </div>
          </div>
        )}

        {/* 2FA Status Card */}
        {!setupStep && (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${twoFactorEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Smartphone className={`w-5 h-5 ${twoFactorEnabled ? 'text-green-600' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-800">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-500">
                    {twoFactorLoading ? 'Checking status...' : twoFactorEnabled ? 'Enabled - Your account is protected' : 'Not enabled - Add extra security'}
                  </p>
                </div>
              </div>
              <div>
                {!twoFactorLoading && (
                  twoFactorEnabled ? (
                    twoFactorMandatory ? (
                      <span className="px-4 py-2 text-sm text-gray-500 bg-gray-100 rounded-md cursor-not-allowed">
                        Mandatory
                      </span>
                    ) : (
                      <button
                        onClick={() => setShowDisableModal(true)}
                        className="px-4 py-2 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50"
                      >
                        Disable
                      </button>
                    )
                  ) : (
                    <button
                      onClick={start2FASetup}
                      className="px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700"
                    >
                      Enable 2FA
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* QR Code Setup Step */}
        {setupStep === 'qr' && (
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="font-medium text-gray-800 mb-4">Step 1: Scan QR Code</h3>
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            {qrCode && (
              <div className="flex justify-center mb-4">
                <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border rounded" />
              </div>
            )}
            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <p className="text-xs text-gray-500 mb-1">Can't scan? Enter this code manually:</p>
              <code className="text-sm font-mono break-all">{manualKey}</code>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSetupStep('verify')}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Next: Verify Code
              </button>
              <button
                onClick={cancel2FASetup}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Verify Code Step */}
        {setupStep === 'verify' && (
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="font-medium text-gray-800 mb-4">Step 2: Verify Setup</h3>
            <p className="text-sm text-gray-600 mb-4">
              Enter the 6-digit code from your authenticator app.
            </p>
            <form onSubmit={verify2FASetup}>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                placeholder="000000"
                maxLength={6}
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={verificationCode.length !== 6 || twoFactorLoading}
                  className="flex-1 px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {twoFactorLoading ? 'Verifying...' : 'Verify & Enable'}
                </button>
                <button
                  type="button"
                  onClick={() => setSetupStep('qr')}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Backup Codes Step */}
        {setupStep === 'backup' && (
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              2FA Enabled Successfully!
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Save these backup codes in a safe place.
            </p>
            <div className="bg-gray-50 p-4 rounded-md mb-4">
              <div className="grid grid-cols-2 gap-2">
                {backupCodes.map((code, index) => (
                  <code key={index} className="text-sm font-mono bg-white px-2 py-1 rounded border">{code}</code>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyBackupCodes}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Copy className="w-4 h-4" />
                Copy Codes
              </button>
              <button
                onClick={finish2FASetup}
                className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Disable 2FA Modal */}
        {showDisableModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-medium text-gray-800 mb-4">Disable Two-Factor Authentication</h3>
              <p className="text-sm text-gray-600 mb-4">
                This will make your account less secure. Please confirm by entering your password and a 2FA code.
              </p>
              <form onSubmit={disable2FA}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">2FA Code</label>
                  <input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                  />
                </div>
                {twoFactorError && (
                  <div className="mb-4 p-2 text-sm text-red-600 bg-red-50 rounded">{twoFactorError}</div>
                )}
                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={twoFactorLoading}
                    className="flex-1 px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {twoFactorLoading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowDisableModal(false); setTwoFactorError(null); setDisablePassword(''); setDisableCode(''); }}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    

{/* ── Danger Zone ─────────────────────────────────────────── */}
      
        <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <h2 className="text-2xl font-bold text-red-600">Danger Zone</h2>
        </div>

        <div className="border border-red-200 rounded-lg p-5 bg-red-50">
          <h3 className="font-semibold text-red-700 mb-1">Delete My Account</h3>
          <p className="text-sm text-gray-600 mb-4">
            Permanently delete your account and all associated data — profile, goals,
            roadmap, documents, connections and platform activity.
            Your email and phone are retained for legal purposes only.
            SkillBot conversations are retained anonymised for AI quality evaluation.
            <strong> This cannot be undone.</strong>
          </p>
         <button
            onClick={() => setShowDeleteModal(true)}
            className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-md hover:bg-red-700 transition-colors"
          >
            Delete My Account Permanently
          </button>
        </div>

      

      {/* ── Delete Account Modal ──────────────────────────────────── */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-md shadow-2xl">

            {/* Step 1 — Warning */}
            {deleteStep === 1 && (
              <>
                <div className="text-center mb-5">
                  <span className="text-5xl">⚠️</span>
                  <h3 className="text-xl font-bold text-red-600 mt-3 mb-1">Delete Your Account?</h3>
                  <p className="text-sm text-gray-500">This is permanent and cannot be undone</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-800">
                  <strong>The following will be permanently deleted:</strong>
                  <ul className="mt-2 space-y-1 list-disc list-inside text-red-700">
                    <li>Profile, bio, photo</li>
                    <li>Goals, roadmap, workspace</li>
                    <li>Uploaded documents and Knowledge Base entries</li>
                    <li>Community connections and messages</li>
                    <li>LinkedIn network data</li>
                  </ul>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-5 text-sm text-green-800">
                  <strong>Retained (anonymised):</strong>
                  <ul className="mt-2 space-y-1 list-disc list-inside text-green-700">
                    <li>Email + phone (legal purposes, internal only)</li>
                    <li>SkillBot conversations (anonymised, AI evaluation)</li>
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 text-sm font-semibold"
                  >
                    Cancel — Keep Account
                  </button>
                  <button
                    onClick={() => setDeleteStep(2)}
                    className="flex-1 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-semibold"
                  >
                    Continue →
                  </button>
                </div>
              </>
            )}

            {/* Step 2 — Confirm */}
            {deleteStep === 2 && (
              <>
                <h3 className="text-xl font-bold text-red-600 mb-4">Confirm Deletion</h3>
                {deleteError && (
                  <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">
                    {deleteError}
                  </div>
                )}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Your Password
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={e => setDeletePassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>

{/* 2FA field — only shown if user has 2FA enabled */}
                {twoFactorEnabled && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      2FA Code <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={deleteTwoFactorCode}
                      onChange={e => setDeleteTwoFactorCode(
                        e.target.value.replace(/\D/g, '').slice(0, 6)
                      )}
                      placeholder="6-digit code from authenticator app"
                      className={`w-full px-3 py-2 border rounded-md text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 ${
                        deleteTwoFactorCode.length === 6
                          ? 'border-green-400 bg-green-50 focus:ring-green-400'
                          : 'border-gray-300 focus:ring-red-400'
                      }`}
                      maxLength={6}
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Your account has 2FA enabled — verification required
                    </p>
                  </div>
                )}

                <div className="mb-5">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Type <span className="text-red-600 font-mono bg-red-50 px-1 rounded">DELETE MY ACCOUNT</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirm}
                    onChange={e => setDeleteConfirm(e.target.value)}
                    placeholder="DELETE MY ACCOUNT"
                    className={`w-full px-3 py-2 border rounded-md text-sm font-mono focus:outline-none focus:ring-2 ${
                      deleteConfirm === 'DELETE MY ACCOUNT'
                        ? 'border-green-400 bg-green-50 focus:ring-green-400'
                        : 'border-gray-300 focus:ring-red-400'
                    }`}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDeleteModal(false); setDeleteStep(1); setDeletePassword(''); setDeleteConfirm(''); setDeleteError(''); }}
                    className="flex-1 py-2 border border-gray-300 rounded-md text-gray-600 text-sm font-semibold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSelfDelete}
                    

                     disabled={
                      deleteConfirm !== 'DELETE MY ACCOUNT' ||
                      !deletePassword ||
                      (twoFactorEnabled && deleteTwoFactorCode.length !== 6) ||
                      deleteLoading
                    }

                    className={`flex-1 py-2 text-white text-sm font-bold rounded-md transition-colors ${
                       
                      deleteConfirm === 'DELETE MY ACCOUNT' &&
                      deletePassword &&
                      (!twoFactorEnabled || deleteTwoFactorCode.length === 6) &&
                      !deleteLoading

                        ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                        : 'bg-red-300 cursor-not-allowed'
                    }`}
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
                  </button>
                </div>
              </>
            )}

            {/* Step 3 — Done */}
            {deleteStep === 3 && (
              <div className="text-center py-4">
                <span className="text-5xl">✅</span>
                <h3 className="text-xl font-bold text-gray-800 mt-4 mb-2">Account Deleted</h3>
                <p className="text-sm text-gray-500">
                  All your data has been permanently removed.<br />
                  A confirmation email has been sent.<br />
                  Redirecting...
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      </div>
        </div>
  );
};


export default ProfileSettings;


